import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import ConsentCheckboxes, { type ConsentValues } from '../components/auth/ConsentCheckboxes';

/**
 * Consent collection page for OAuth users (Google / Apple).
 *
 * Shown once — the first time an OAuth user signs in — before they
 * enter the main app. Collects:
 *   - Phone number (required for SMS reminders)
 *   - Name confirmation (pre-filled from OAuth metadata)
 *   - Three consent checkboxes
 *
 * After submission, the user's profile is marked complete and they
 * proceed to the dashboard. Never shown again.
 */
export default function OAuthConsentPage() {
  const navigate               = useNavigate();
  const { user, profile, loading, refreshProfile } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [phone,     setPhone]     = useState('');
  const [consent, setConsent]     = useState<ConsentValues>({
    consentSms: false, consentTerms: false, marketingOptIn: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  // Redirect unauthenticated users
  useEffect(() => {
    if (!loading && !user) navigate('/login', { replace: true });
  }, [user, loading, navigate]);

  // Redirect users who already completed consent
  useEffect(() => {
    if (!loading && profile?.consent_terms_ts && profile?.consent_sms_ts) {
      navigate('/dashboard', { replace: true });
    }
  }, [profile, loading, navigate]);

  // Pre-fill name from profile (populated by DB trigger from OAuth metadata)
  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name === 'Friend' ? '' : profile.first_name);
      setLastName(profile.last_name ?? '');
    }
  }, [profile]);

  const canSubmit =
    firstName.trim() &&
    phone.trim() &&
    consent.consentSms &&
    consent.consentTerms;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);

    try {
      await apiFetch('/api/auth/update-consent', {
        method: 'POST',
        body:   JSON.stringify({
          firstName:      firstName.trim(),
          lastName:       lastName.trim(),
          phone:          phone.trim(),
          consentSms:     consent.consentSms,
          consentTerms:   consent.consentTerms,
          marketingOptIn: consent.marketingOptIn,
        }),
      });

      await refreshProfile();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-sage border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-4">

      {/* Logo */}
      <Link to="/" className="mb-8 font-heading text-3xl font-bold text-navy hover:opacity-80 transition-opacity">
        Moment<span className="text-sage">Sent</span>
      </Link>

      {/* Card */}
      <div className="w-full max-w-md bg-white rounded-card shadow-warm-lg p-8">

        {/* Header */}
        <div className="text-center mb-7">
          <div className="text-4xl mb-3">💌</div>
          <h1 className="font-heading text-2xl text-navy mb-2">
            One last thing
          </h1>
          <p className="font-body text-sm text-gray-500 leading-relaxed">
            Before we get started, we need a couple of details,
            including your phone number for card reminder SMS.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-3">

          {/* Name row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="oFirstName" className="sr-only">First name</label>
              <input
                id="oFirstName"
                type="text"
                placeholder="First name"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                disabled={submitting}
                className="input"
              />
            </div>
            <div>
              <label htmlFor="oLastName" className="sr-only">Last name</label>
              <input
                id="oLastName"
                type="text"
                placeholder="Last name"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={submitting}
                className="input"
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="oPhone" className="sr-only">Phone number</label>
            <input
              id="oPhone"
              type="tel"
              placeholder="Phone number (for card reminders)"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              disabled={submitting}
              className="input"
            />
          </div>

          {/* Consent */}
          <div className="pt-1">
            <ConsentCheckboxes
              values={consent}
              onChange={setConsent}
              disabled={submitting}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 animate-fade-in">
              <p className="font-body text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className="w-full btn-primary flex items-center justify-center gap-2
                       disabled:opacity-50 disabled:cursor-not-allowed
                       disabled:hover:shadow-none disabled:hover:translate-y-0"
          >
            {submitting ? (
              <><Loader2 size={16} className="animate-spin" /><span>Saving…</span></>
            ) : (
              <span>Let's go →</span>
            )}
          </button>

        </form>

      </div>
    </div>
  );
}
